import type { FC, MouseEvent, ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'

import {
    AnimatePresence,
    motion,
    useMotionValueEvent,
    useScroll
} from 'motion/react'

import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { Logotype } from '@/components/generals/brand'
import { RollText } from '@/components/generals/roll-text'
import { scrollToTarget, useLenis } from '@/components/generals/smooth-scroll'
import { navigation, siteConfig } from '@/constants/site'

const menuItems = [...navigation, { label: "Bashkohu", href: '#bashkohu' }]

export const Header: FC = (): ReactNode => {
    const { pathname } = useRouter()
    const isHome = pathname === '/'
    const lenis = useLenis()
    const toggleRef = useRef<HTMLButtonElement>(null)
    const [hidden, setHidden] = useState(false)
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)
    const { scrollY } = useScroll()

    // Hide while scrolling down, come back as soon as the visitor scrolls up.
    useMotionValueEvent(scrollY, 'change', (latest) => {
        const previous = scrollY.getPrevious() ?? 0

        setScrolled(latest > 24)
        setHidden(latest > previous && latest > 320)
    })

    useEffect(() => {
        if (!open) return

        const root = document.documentElement
        const previousOverflow = root.style.overflow
        const toggle = toggleRef.current

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false)
        }

        lenis?.stop()
        root.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)

        return () => {
            lenis?.start()
            root.style.overflow = previousOverflow
            window.removeEventListener('keydown', onKeyDown)
            toggle?.focus()
        }
    }, [open, lenis])

    const hrefFor = (hash: string): string => (isHome ? hash : `/${hash}`)

    const onMenuLink = (hash: string) => (event: MouseEvent<HTMLAnchorElement>) => {
        setOpen(false)

        if (!isHome) return

        event.preventDefault()
        window.setTimeout(() => scrollToTarget(lenis, hash), 420)
    }

    return (
        <>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-[translate,background-color,box-shadow,color] duration-500 ease-out-expo',
                    open ? 'text-jap-paper' : 'text-tone-fg',
                    hidden && !open && '-translate-y-full',
                    scrolled && !open && 'bg-tone-bg/80 shadow-[0_1px_0_var(--tone-line)] backdrop-blur-md'
                )}
            >
                <div className='flex h-16 items-center justify-between gap-6 px-5 md:h-20 md:px-8 lg:px-12'>
                    <Link
                        href='/'
                        aria-label="JAP, faqja kryesore"
                        className='group flex items-center'
                    >
                        <Logotype
                            className='h-5 w-auto md:h-6'
                            chevronClassName={cn(
                                'transition-transform duration-500 ease-out-expo group-hover:translate-x-[14px]',
                                open ? 'text-jap-gold' : 'text-tone-accent'
                            )}
                        />
                    </Link>

                    <nav aria-label="Navigimi kryesor" className='hidden lg:block'>
                        <ul className='flex items-center gap-10'>
                            {navigation.map((item, index) => (
                                <li key={item.href}>
                                    <a
                                        href={hrefFor(item.href)}
                                        className='group flex items-baseline gap-2 text-[0.9rem] font-medium'
                                    >
                                        <span className='font-heading text-[0.65rem] tabular-nums opacity-60'>
                                            {pad(index + 1)}
                                        </span>

                                        <RollText text={item.label} />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='flex items-center gap-2'>
                        <a
                            href={hrefFor('#bashkohu')}
                            className={cn(
                                'group hidden items-center gap-2 px-4 py-2.5 text-[0.9rem] font-semibold transition-colors sm:inline-flex',
                                open ? 'bg-jap-gold text-jap-ink' : 'bg-tone-accent text-tone-accent-fg'
                            )}
                        >
                            <RollText text="Bashkohu" />
                            <ArrowUpRight className='size-4 transition-transform duration-500 ease-out-expo group-hover:rotate-45' />
                        </a>

                        <button
                            ref={toggleRef}
                            type='button'
                            onClick={() => setOpen((value) => !value)}
                            aria-expanded={open}
                            aria-controls='menu'
                            className='group flex h-10 items-center gap-3 px-2 text-[0.9rem] font-semibold lg:hidden'
                        >
                            <span>{open ? "Mbyll" : "Menu"}</span>

                            <span aria-hidden='true' className='relative block h-3 w-6'>
                                <span
                                    className={cn(
                                        'absolute left-0 top-0 h-[2px] w-full bg-current transition-transform duration-500 ease-out-expo',
                                        open && 'translate-y-[5px] rotate-45'
                                    )}
                                />
                                <span
                                    className={cn(
                                        'absolute bottom-0 left-0 h-[2px] w-full bg-current transition-transform duration-500 ease-out-expo',
                                        open && '-translate-y-[5px] -rotate-45'
                                    )}
                                />
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        id='menu'
                        role='dialog'
                        aria-modal='true'
                        aria-label="Menuja"
                        initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                        animate={{ clipPath: 'inset(0% 0% 0% 0%)' }}
                        exit={{ clipPath: 'inset(0% 0% 100% 0%)' }}
                        transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
                        className='fixed inset-0 z-40 flex flex-col overflow-y-auto bg-jap-purple px-5 pb-8 pt-24 text-jap-paper md:px-8 lg:hidden'
                    >
                        <nav aria-label="Menuja">
                            <ol>
                                {menuItems.map((item, index) => (
                                    <motion.li
                                        key={item.href}
                                        initial={{ y: 48, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.18 + index * 0.06, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                                        className='border-b border-jap-paper/15'
                                    >
                                        <a
                                            href={hrefFor(item.href)}
                                            onClick={onMenuLink(item.href)}
                                            autoFocus={index === 0}
                                            className='flex items-baseline gap-4 py-3 font-heading text-[13vw] font-extrabold leading-[1.05] tracking-[-0.04em] [font-stretch:110%] sm:text-7xl'
                                        >
                                            <span className='font-sans text-xs font-medium tracking-normal text-jap-lilac'>
                                                {pad(index + 1)}
                                            </span>

                                            {item.label}
                                        </a>
                                    </motion.li>
                                ))}
                            </ol>
                        </nav>

                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.55, duration: 0.6 }}
                            className='mt-auto flex flex-col gap-3 pt-10 text-sm text-jap-lilac'
                        >
                            <p className='font-serif text-2xl italic text-jap-gold'>
                                {siteConfig.tagline}
                            </p>

                            <a href={`mailto:${siteConfig.email}`} className='font-medium text-jap-paper underline underline-offset-4'>
                                {siteConfig.email}
                            </a>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}
